import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material'

import { actions } from 'modules/home/HomeReducer'
import { getSpecialtyInput, getSearchPanel } from 'modules/home/HomeSelector'
import { actions as authActions } from 'modules/auth/AuthReducer'
import { getIsLogin, getProfile } from 'modules/auth/AuthSelector'
import { getOptinSpecialtyLIMAtoCONTENT } from 'modules/optin/OptinSelector'
import { getProfileInfo } from 'utils'
import {
  iconCredits,
  iconDropdown,
  iconSearch,
  iconMenu
} from 'components/Icons'

import HeaderLink from 'components/HeaderLink'
import HeaderMenu from 'components/HeaderMenu'
import SearchBar from 'components/SearchBar'
import styles from './Header.module.sass'
import config from 'site.config'

const { openLoginModal } = authActions

const isActivationKey = key => key === 'Enter' || key === ' '

const Menu = ({
  route,
  icon,
  iconSrc,
  title,
  alt,
  width,
  requireAuth,
  onClick,
  menuOpen,
  exceptMobile,
  onlyMobile,
  pathname
}) => {
  const isActive = pathname === route

  return (
    <div
      role="button"
      tabIndex={0}
      alt={alt}
      className={classNames(styles.menuItem, {
        [styles.active]: isActive,
        [styles.menuOpen]: menuOpen,
        [styles.exceptMobile]: exceptMobile,
        [styles.onlyMobile]: onlyMobile
      })}
      style={{ width }}
      onClick={() => onClick(route, requireAuth)}
      onKeyDown={e => {
        if (isActivationKey(e.key)) onClick(route, requireAuth)
      }}
    >
      {icon({
        style: { height: 20 },
        className: classNames(styles.btnimg, {
          [styles.active]: isActive,
          [styles.menuOpen]: menuOpen
        }),
        alt: alt
      })}
      {title && <label aria-label={alt}>{title}</label>}
    </div>
  )
}

Menu.propTypes = {
  route: PropTypes.string,
  icon: PropTypes.func,
  iconSrc: PropTypes.string,
  title: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.string,
  requireAuth: PropTypes.bool,
  menuOpen: PropTypes.bool,
  exceptMobile: PropTypes.bool,
  onlyMobile: PropTypes.bool,
  onClick: PropTypes.func,
  pathname: PropTypes.string
}

const DropdownIcon = props => (
  <svg
    style={{
      fill: '#fff',
      width: '1em',
      height: '1em',
      fontSize: '1.5em',
      position: 'absolute',
      right: '7px',
      top: 'calc(50% - 0.5em)',
      pointerEvents: 'none',
      display: 'inline-block',
      transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1)'
    }}
    focusable="false"
    aria-hidden="true"
    viewBox="0 0 24 24"
    data-testid="ExpandMoreIcon"
    {...props}
  >
    <path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z"></path>
  </svg>
)

const Header = props => {
  const dispatch = useDispatch()
  const history = useHistory()
  const pathname = history?.location?.pathname || ''
  const [menuOpen, setMenuOpen] = useState(false)
  const searchOpen = useSelector(getSearchPanel)

  // Specialty
  const specialtyInput = useSelector(getSpecialtyInput)
  const specialtyOptions = useSelector(getOptinSpecialtyLIMAtoCONTENT)

  const isLoginUser = useSelector(getIsLogin)
  const profile = useSelector(getProfile)

  useEffect(() => {
    if (isLoginUser && profile) {
      const { specialtyId } = getProfileInfo(profile)
      const newValue = specialtyOptions.find(f => f.value === specialtyId)
      if (specialtyId && newValue) {
        dispatch({ type: 'SET_SPECIALTY_INPUT', payload: newValue })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isLoginUser, profile, specialtyOptions?.length])

  useEffect(() => {
    setMenuOpen(props.menuOpen === true)
  }, [props.menuOpen])

  const checkValidateBeforeRoute = useCallback(
    (route, requireAuth) => {
      if (requireAuth && !isLoginUser) {
        dispatch(openLoginModal({ route }))
        return
      }
      history.push(route)
    },
    [dispatch, history, isLoginUser]
  )

  const toggleMenu = useCallback(() => {
    const currentState = menuOpen
    const element = document.getElementById('header-menu')
    if (element) {
      element.style.display = 'block'
      setTimeout(() => {
        element.style.display = currentState ? 'none' : 'block'
      }, 300)
    }
    setTimeout(() => {
      setMenuOpen(!menuOpen)
    }, 10)
  }, [menuOpen])

  const toggleSearch = useCallback(() => {
    setMenuOpen(false)
    dispatch(actions.setSearchPanel(!searchOpen))
  }, [dispatch, searchOpen])

  const [width, setWidth] = useState(window.innerWidth)

  useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  const isDesktop = width >= 1040

  const specialtyValue = useMemo(
    () => (specialtyOptions?.length ? specialtyInput?.value || '' : ''),
    [specialtyInput?.value, specialtyOptions?.length]
  )

  const handleSpecialtyChange = useCallback(
    e => {
      const newValue = e.target.value
      const selectedOption = specialtyOptions.find(f => f.value === newValue)
      if (specialtyInput && selectedOption) {
        dispatch({ type: 'SET_SPECIALTY_INPUT', payload: selectedOption })
      }
    },
    [dispatch, specialtyInput, specialtyOptions]
  )

  return (
    <header className={styles.bg}>
      <div id="header" className={styles.box}>
        <div
          role="button"
          tabIndex={0}
          className={styles.dleft}
          onClick={() => setMenuOpen(false)}
          onKeyDown={e => {
            if (isActivationKey(e.key)) setMenuOpen(false)
          }}
        >
          <HeaderLink
            styles={styles}
            logo={config.logo}
            alt={config.siteName}
            tagline={config.logo.tagline}
          />
          {isDesktop && <SearchBar />}
        </div>
        <div
          role="button"
          tabIndex={0}
          className={styles.dcenter}
          onClick={() => setMenuOpen(false)}
          onKeyDown={e => {
            if (isActivationKey(e.key)) setMenuOpen(false)
          }}
        >
          {(!searchOpen || isDesktop) && (
            <FormControl
              sx={{
                width: '100%',
                '& .Mui-focused': {
                  color: '#fff !important'
                },
                '& label': {
                  marginTop: '-8px'
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, -1px) scale(0.75)'
                }
              }}
            >
              <InputLabel
                id="ddSpecialty-label"
                sx={{ color: '#fff', fontFamily: '"PT Sans", sans-serif' }}
              >
                Area of Medicine
              </InputLabel>
              <Select
                notched
                label="Area of Medicine"
                labelId="ddSpecialty-label"
                id="ddSpecialty"
                value={specialtyValue}
                IconComponent={DropdownIcon}
                sx={{
                  width: '100%',
                  fontFamily: '"PT Sans", sans-serif',
                  '& .MuiInputLabel-root': {
                    color: '#fff',
                    borderColor: '#fff',
                    fontFamily: '"PT Sans", sans-serif',
                    fontSize: '16px',
                    lineHeight: '22px'
                  },
                  '& .MuiInputBase-input': {
                    color: '#fff',
                    fontFamily: '"PT Sans", sans-serif'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '30px',
                    border: 'solid 1.5px #fff !important',
                    ' legend': {
                      fontSize: '12px',
                      lineHeight: '18px',
                      width: '100px'
                    }
                  },
                  '.MuiSvgIcon-root': {
                    color: '#fff'
                  }
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 460 } } }}
                size="small"
                onChange={handleSpecialtyChange}
                onOpen={() => setMenuOpen(false)}
              >
                {specialtyOptions?.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      width: '100%',
                      fontSize: '16px',
                      lineHeight: '22px',
                      fontFamily: '"PT Sans", sans-serif'
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {searchOpen && !isDesktop && <SearchBar />}
        </div>
        <div className={styles.dright}>
          <Menu
            route="/cme-tracker"
            icon={iconCredits}
            title="CME Tracker"
            alt="CME Tracker"
            requireAuth
            onClick={checkValidateBeforeRoute}
            width={width < 1040 ? '90px' : 'unset'}
            pathname={pathname}
          />
          <Menu
            icon={searchOpen ? iconDropdown : iconSearch}
            onClick={toggleSearch}
            alt="search"
            onlyMobile
            pathname={pathname}
          />
          <Menu
            icon={iconMenu}
            menuOpen={menuOpen}
            onClick={toggleMenu}
            alt="menu"
            pathname={pathname}
          />
          <HeaderMenu open={menuOpen} closeMenu={() => setMenuOpen(false)} />
        </div>
      </div>
    </header>
  )
}

Header.propTypes = {
  menuOpen: PropTypes.any
}

export default Header
